import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import productService from '../services/productService';
import measureService from '../services/measureService';
import { useNotification } from '../context/NotificationContext';

const PRODUCT_TYPE_CHOICES = [
  { value: 'rollo', label: 'Rollo' },
  { value: 'estructura', label: 'Estructura' },
  { value: 'producto general', label: 'Producto General' },
  { value: 'servicio', label: 'Servicio' },
  { value: 'otro', label: 'Otro' }
];

const ProductFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_type: 'otro',
    brand: '',
    cost: '',
    price: '',
    measure_id: ''
  });

  const [measures, setMeasures] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const measuresRes = await measureService.listMeasures();
        setMeasures(measuresRes.data.results || []);

        if (isEditMode) {
          const productRes = await productService.getProduct(id);
          const product = productRes.data;
          setFormData({
            name: product.name,
            description: product.description || '',
            product_type: product.product_type,
            brand: product.brand,
            cost: product.cost,
            price: product.price,
            measure_id: product.measure.id
          });
        }
      } catch (err) {
        notify.error(t('common.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode, notify, t]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.brand.trim()) newErrors.brand = 'Brand is required';
    if (!formData.product_type) newErrors.product_type = 'Product type is required';
    if (!formData.cost || isNaN(formData.cost)) newErrors.cost = 'Valid cost is required';
    if (!formData.price || isNaN(formData.price)) newErrors.price = 'Valid price is required';
    if (!formData.measure_id) newErrors.measure_id = 'Measure is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (isEditMode) {
        await productService.updateProduct(id, formData);
        notify.success(t('common.updatedSuccessfully'));
      } else {
        await productService.createProduct(formData);
        notify.success(t('common.createdSuccessfully'));
      }
      setTimeout(() => navigate('/products'), 2000);
    } catch (err) {
      notify.error(err.response?.data?.detail || t('common.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#3D3D3D' }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px' }}>
      <h1 style={{ color: '#3D3D3D' }}>
        {isEditMode ? `Edit Product: ${formData.name}` : 'Create New Product'}
      </h1>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
            Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.name ? '2px solid #F22B55' : '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
          {errors.name && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.name}</p>}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box',
              fontFamily: 'inherit'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
            Product Type *
          </label>
          <select
            name="product_type"
            value={formData.product_type}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.product_type ? '2px solid #F22B55' : '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          >
            {PRODUCT_TYPE_CHOICES.map(choice => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          {errors.product_type && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.product_type}</p>}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
            Brand *
          </label>
          <input
            type="text"
            name="brand"
            value={formData.brand}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.brand ? '2px solid #F22B55' : '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
          {errors.brand && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.brand}</p>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
              Cost *
            </label>
            <input
              type="number"
              name="cost"
              value={formData.cost}
              onChange={handleChange}
              step="0.01"
              min="0"
              style={{
                width: '100%',
                padding: '10px',
                border: errors.cost ? '2px solid #F22B55' : '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
            {errors.cost && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.cost}</p>}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
              Price *
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.01"
              min="0"
              style={{
                width: '100%',
                padding: '10px',
                border: errors.price ? '2px solid #F22B55' : '1px solid #ddd',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
            {errors.price && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.price}</p>}
          </div>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: '#3D3D3D', fontWeight: 'bold' }}>
            Measure *
          </label>
          <select
            name="measure_id"
            value={formData.measure_id}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '10px',
              border: errors.measure_id ? '2px solid #F22B55' : '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          >
            <option value="">Select a measure</option>
            {measures.map(measure => (
              <option key={measure.id} value={measure.id}>
                {measure.name} ({measure.abbreviation})
              </option>
            ))}
          </select>
          {errors.measure_id && <p style={{ color: '#F22B55', fontSize: '12px', marginTop: '5px' }}>{errors.measure_id}</p>}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#A0BD46',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Product' : 'Create Product')}
          </button>

          <button
            type="button"
            onClick={() => navigate('/products')}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#3D3D3D',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductFormPage;
